import clsx from 'clsx'

import { ChildProps } from '@/common/util/props'

/**
 * 使えるフォントサイズ。
 *
 * ここは以前 `const TEXT_SIZE = 10 | 12 | ...` と書かれていたが、これは型の合併ではなく
 * **数値のビット演算**で、値は 63、`typeof TEXT_SIZE` は `number` になっていた。
 * そのため size の検査が実質効かず、下の switch に無い 11 や 15 を渡してもエラーにならず、
 * クラスが付かないまま既定サイズで表示されていた。型として宣言し直す。
 */
export type TextSize = 10 | 12 | 13 | 14 | 16 | 20 | 24 | 32 | 40

const textSize = (size: TextSize) => {
  switch (size) {
    case 10:
      return 'text-size-10'
    case 12:
      return 'text-size-12'
    case 13:
      return 'text-size-13'
    case 14:
      return 'text-size-14'
    case 16:
      return 'text-size-16'
    case 20:
      return 'text-size-20'
    case 24:
      return 'text-size-24'
    case 32:
      return 'text-size-32'
    case 40:
      return 'text-size-40'
  }
}

export const TextColor = {
  PRIMARY: 'primary',
  SECONDARY: 'secondary',
  TERTIARY: 'tertiary',
  PLACEHOLDER: 'placeholder',
  DISABLED: 'disabled',
  LINK: 'link',
  INFO: 'info',
  SUCCESS: 'success',
  SUCCESS_ON_BG_FILL: 'success-on-bg-fill',
  CAUTION: 'caution',
  WARNING: 'warning',
  CRITICAL: 'critical',
  CRITICAL_ON_BG_FILL: 'critical-on-bg-fill',
  EMPHASIS: 'emphasis',
  ASSIST: 'assist',
  ASSIST_SECONDARY: 'assist-secondary',
  ON_BG_FILL: 'on-bg-fill',
  ON_BG_FILL_SECONDARY: 'on-bg-fill-secondary',
  PURPLE: 'purple',
  SKY: 'sky',
  GREEN: 'green',
  YELLOW: 'yellow',
  ORANGE: 'orange',
  BENI: 'beni',
  SORA: 'sora',
  KAKI: 'kaki',
  BRAND: 'brand',
  AI: 'ai'
} as const
export type TextColor = (typeof TextColor)[keyof typeof TextColor]

const textColor = (size: TextColor) => {
  switch (size) {
    case TextColor.PRIMARY:
      return 'text-primary'
    case TextColor.SECONDARY:
      return 'text-secondary'
    case TextColor.TERTIARY:
      return 'text-sumi-600'
    case TextColor.PLACEHOLDER:
      return 'text-placeholder'
    case TextColor.DISABLED:
      return 'text-disabled'
    case TextColor.LINK:
      return 'text-link'
    case TextColor.INFO:
      return 'text-info'
    case TextColor.SUCCESS:
      return 'text-success'
    case TextColor.SUCCESS_ON_BG_FILL:
      return 'text-success-on-bg-fill'
    case TextColor.CAUTION:
      return 'text-caution'
    case TextColor.WARNING:
      return 'text-warning'
    case TextColor.CRITICAL:
      return 'text-critical'
    case TextColor.CRITICAL_ON_BG_FILL:
      return 'text-critical-on-bg-fill'
    case TextColor.EMPHASIS:
      return 'text-emphasis'
    case TextColor.ASSIST:
      return 'text-assist'
    case TextColor.ASSIST_SECONDARY:
      return 'text-assist-secondary'
    case TextColor.ON_BG_FILL:
      return 'text-on-bg-fill'
    case TextColor.ON_BG_FILL_SECONDARY:
      return 'text-on-bg-fill-secondary'
    case TextColor.PURPLE:
      return 'text-purple'
    case TextColor.SKY:
      return 'text-sky'
    case TextColor.GREEN:
      return 'text-green'
    case TextColor.YELLOW:
      return 'text-yellow'
    case TextColor.ORANGE:
      return 'text-orange'
    case TextColor.BENI:
      return 'text-beni'
    case TextColor.SORA:
      return 'text-sora'
    case TextColor.KAKI:
      return 'text-kaki'
    case TextColor.BRAND:
      return 'text-fill-brand'
    case TextColor.AI:
      return 'text-ai'
  }
}

const textWeight = (weight: 'normal' | 'bold') => {
  switch (weight) {
    case 'normal':
      return 'font-normal'
    case 'bold':
      return 'font-bold'
  }
}

const textLineHeight = (lineHeight: 100 | 120 | 150) => {
  switch (lineHeight) {
    case 100:
      return 'leading-[100%]'
    case 120:
      return 'leading-[120%]'
    case 150:
      return 'leading-[150%]'
  }
}

const textAlign = (align: 'left' | 'center' | 'right') => {
  switch (align) {
    case 'left':
      return 'text-left'
    case 'center':
      return 'text-center'
    case 'right':
      return 'text-right'
  }
}

interface Props extends ChildProps {
  size?: TextSize
  color?: TextColor
  weight?: 'normal' | 'bold'
  lineHeight?: 100 | 120 | 150
  tag?: 'span' | 'p' | 'h1' | 'h2' | 'h3'
  nowrap?: boolean
  preWrap?: boolean
  align?: 'left' | 'center' | 'right'
  truncate?: boolean
  dotted?: boolean
  indent?: boolean
  label?: boolean
  lineThrough?: boolean
  fullWidth?: boolean
}

const Text = ({
  children,
  size = 14,
  color = TextColor.PRIMARY,
  weight = 'normal',
  lineHeight = 120,
  tag = 'span',
  nowrap = false,
  preWrap = false,
  align = 'left',
  truncate = false,
  dotted = false,
  indent = false,
  label = false,
  lineThrough = false,
  fullWidth
}: Props) => {
  const Tag = tag
  return (
    <Tag
      className={clsx(
        textSize(size),
        textColor(color),
        textWeight(weight),
        textLineHeight(lineHeight),
        nowrap ? 'whitespace-nowrap' : 'whitespace-normal',
        preWrap ? 'whitespace-pre-wrap' : 'whitespace-normal',
        textAlign(align),
        truncate && 'block truncate',
        dotted &&
          'after:border-tertiary relative pb-[2px] after:absolute after:bottom-0 after:left-0 after:w-full after:border-b-2 after:border-dotted',
        indent && 'pl-2',
        label && 'inline-flex min-h-[26px] items-center',
        lineThrough && 'line-through',
        fullWidth && 'w-full'
      )}
    >
      {children}
    </Tag>
  )
}

export default Text
